import { PERMISSIONS, type PermissionCode } from '@oxxean/types';

/**
 * Declaración de las tablas de detalle de cada módulo de indicadores.
 *
 * `kind` describe cómo se presenta la celda; `metric` significa que el backend
 * envía un `MetricValue` completo y la celda se delega a `<MetricDisplay>`, con lo
 * que el estado del dato llega hasta la última fila de la tabla.
 */
export type IndicatorColumnKind = 'text' | 'integer' | 'decimal' | 'percent' | 'date' | 'metric';

export interface IndicatorColumnConfig {
  key: string;
  header: string;
  kind: IndicatorColumnKind;
  sortable?: boolean;
  help?: string;
  /** Formato aplicado cuando `kind` es `metric`. */
  metricFormat?: 'integer' | 'decimal' | 'percent';
}

export interface IndicatorModuleConfig {
  /** Clave del módulo en el backend: `/api/metrics/{key}/…` y `/api/exports/{key}`. */
  key: string;
  title: string;
  description: string;
  permission: PermissionCode;
  tableTitle: string;
  searchPlaceholder: string;
  columns: IndicatorColumnConfig[];
  /** Aviso metodológico permanente de la pantalla, cuando corresponde. */
  methodNote?: string;
  defaultSort?: string;
}

const CONTEXT_COLUMNS: IndicatorColumnConfig[] = [
  { key: 'periodLabel', header: 'Período', kind: 'text', sortable: true },
  { key: 'companyLabel', header: 'Empresa', kind: 'text', sortable: true },
  { key: 'areaLabel', header: 'Área', kind: 'text', sortable: true },
];

export const HEADCOUNT_MODULE: IndicatorModuleConfig = {
  key: 'headcount',
  title: 'Dotación',
  description:
    'Dotación de inicio, cierre y promedio por empresa y área. La dotación es un stock: se suma entre ámbitos del mismo mes, nunca a lo largo del tiempo.',
  permission: PERMISSIONS.HEADCOUNT_VIEW,
  tableTitle: 'Detalle de dotación por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'La dotación promedio es la media de los extremos del período (inicio y cierre), no el promedio diario.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'headcountStart', header: 'Dotación inicio', kind: 'integer', sortable: true },
    { key: 'hires', header: 'Ingresos', kind: 'integer', sortable: true },
    { key: 'exits', header: 'Egresos', kind: 'integer', sortable: true },
    { key: 'headcountEnd', header: 'Dotación cierre', kind: 'integer', sortable: true },
    {
      key: 'headcountAverage',
      header: 'Dotación promedio',
      kind: 'metric',
      metricFormat: 'decimal',
      sortable: true,
    },
  ],
};

export const MOVEMENTS_MODULE: IndicatorModuleConfig = {
  key: 'movements',
  title: 'Ingresos y egresos',
  description:
    'Flujo de personas del período y su conciliación con los documentos emitidos. Ingresos y egresos se tratan como medidas independientes de contratos y finiquitos.',
  permission: PERMISSIONS.HEADCOUNT_VIEW,
  tableTitle: 'Movimientos por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'Un ingreso no se deriva de un contrato emitido ni un egreso de un finiquito: cuando difieren, la diferencia es en sí misma un indicador de gestión y se muestra como descuadre.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'hires', header: 'Ingresos', kind: 'integer', sortable: true },
    { key: 'exits', header: 'Egresos', kind: 'integer', sortable: true },
    { key: 'netChange', header: 'Variación neta', kind: 'integer', sortable: true },
    { key: 'contractsIssued', header: 'Contratos emitidos', kind: 'integer', sortable: true },
    { key: 'settlements', header: 'Finiquitos', kind: 'integer', sortable: true },
    {
      key: 'reconciliation',
      header: 'Descuadre',
      kind: 'metric',
      metricFormat: 'integer',
      help: 'Diferencia entre movimientos de personas y documentos emitidos en el período.',
    },
  ],
};

export const TURNOVER_MODULE: IndicatorModuleConfig = {
  key: 'turnover',
  title: 'Rotación',
  description:
    'Egresos del período sobre la dotación promedio del mismo ámbito. Los porcentajes agregados se calculan como razón de sumas.',
  permission: PERMISSIONS.TURNOVER_VIEW,
  tableTitle: 'Rotación por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'La rotación de un ámbito superior no es el promedio de las rotaciones de sus hijos: se recalcula desde los conteos crudos, porque promediar daría el mismo peso a un área de 6 personas que a una de 216.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'exits', header: 'Egresos', kind: 'integer', sortable: true },
    {
      key: 'headcountAverage',
      header: 'Dotación promedio',
      kind: 'metric',
      metricFormat: 'decimal',
    },
    { key: 'turnoverRate', header: 'Rotación', kind: 'metric', metricFormat: 'percent', sortable: true },
  ],
};

export const ATTENDANCE_MODULE: IndicatorModuleConfig = {
  key: 'attendance',
  title: 'Asistencia',
  description:
    'Días trabajados sobre días laborales esperados y calidad del registro de marcaciones.',
  permission: PERMISSIONS.ATTENDANCE_VIEW,
  tableTitle: 'Asistencia por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'Una marcación omitida y un fallo de sincronización son cosas distintas y se informan por separado: la primera es un problema de registro, el segundo de integración.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'expectedWorkDays', header: 'Días laborales esperados', kind: 'integer', sortable: true },
    { key: 'workedDays', header: 'Días trabajados', kind: 'integer', sortable: true },
    { key: 'markCount', header: 'Marcaciones', kind: 'integer', sortable: true },
    {
      key: 'missingMarks',
      header: 'Marcaciones omitidas',
      kind: 'metric',
      metricFormat: 'integer',
      help: 'Marcaciones esperadas que no llegaron desde el origen.',
    },
    {
      key: 'attendanceRate',
      header: 'Asistencia',
      kind: 'metric',
      metricFormat: 'percent',
      sortable: true,
    },
  ],
};

export const ABSENCE_MODULE: IndicatorModuleConfig = {
  key: 'absence',
  title: 'Ausentismo',
  description:
    'Días de licencia y de ausencia de otro origen sobre los días laborales esperados del período.',
  permission: PERMISSIONS.ABSENCE_VIEW,
  tableTitle: 'Ausentismo por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'El denominador son los días laborales esperados según jornada y feriados legales. Mientras el calendario de feriados no esté cargado, el indicador se informa como parcial.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'leaveCount', header: 'Licencias', kind: 'integer', sortable: true },
    { key: 'leaveDays', header: 'Días de licencia', kind: 'integer', sortable: true },
    { key: 'otherAbsenceDays', header: 'Días otras ausencias', kind: 'integer', sortable: true },
    { key: 'expectedWorkDays', header: 'Días laborales', kind: 'integer', sortable: true },
    {
      key: 'absenceRate',
      header: 'Ausentismo',
      kind: 'metric',
      metricFormat: 'percent',
      sortable: true,
    },
  ],
};

export const LATENESS_MODULE: IndicatorModuleConfig = {
  key: 'lateness',
  title: 'Atrasos',
  description: 'Atrasos registrados y minutos acumulados por empresa y área.',
  permission: PERMISSIONS.LATENESS_VIEW,
  tableTitle: 'Atrasos por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'latenessCount', header: 'Atrasos', kind: 'integer', sortable: true },
    { key: 'latenessMinutes', header: 'Minutos acumulados', kind: 'integer', sortable: true },
    {
      key: 'latenessPerEmployee',
      header: 'Atrasos por persona',
      kind: 'metric',
      metricFormat: 'decimal',
      sortable: true,
    },
  ],
};

export const OVERTIME_MODULE: IndicatorModuleConfig = {
  key: 'overtime',
  // Rótulo obligatorio: «aprobadas», nunca «pagadas».
  title: 'Horas extra aprobadas',
  description:
    'Horas extra aprobadas por empresa y área. La plataforma no dispone del dato de horas pagadas y por tanto no lo informa.',
  permission: PERMISSIONS.OVERTIME_VIEW,
  tableTitle: 'Horas extra aprobadas por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'El origen entrega horas extra APROBADAS. Rotularlas como «pagadas» afirmaría un hecho de remuneración que esta plataforma no puede verificar.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    {
      key: 'approvedOvertimeHours',
      header: 'Horas extra aprobadas',
      kind: 'metric',
      metricFormat: 'decimal',
      sortable: true,
    },
    {
      key: 'overtimePerEmployee',
      header: 'Horas por persona',
      kind: 'metric',
      metricFormat: 'decimal',
      sortable: true,
    },
  ],
};

export const VACATION_MODULE: IndicatorModuleConfig = {
  key: 'vacation',
  title: 'Vacaciones',
  description:
    'Días de vacaciones pendientes y trabajadores con más de dos períodos acumulados.',
  permission: PERMISSIONS.VACATION_VIEW,
  tableTitle: 'Vacaciones por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'El criterio de alerta vigente es «trabajadores con más de dos períodos por encima del 10 % de la dotación promedio», pendiente de validación por la Gerencia de Personas.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'pendingVacationDays', header: 'Días pendientes', kind: 'integer', sortable: true },
    {
      key: 'employeesTwoPeriods',
      header: 'Con más de 2 períodos',
      kind: 'integer',
      sortable: true,
    },
    {
      key: 'pendingDaysPerEmployee',
      header: 'Días por persona',
      kind: 'metric',
      metricFormat: 'decimal',
      sortable: true,
    },
  ],
};

export const DOCUMENTS_MODULE: IndicatorModuleConfig = {
  key: 'documents',
  title: 'Documentación laboral',
  description:
    'Cumplimiento de firma de contratos y anexos, y finiquitos emitidos dentro de plazo.',
  permission: PERMISSIONS.DOCUMENTS_VIEW,
  tableTitle: 'Cumplimiento documental por empresa y área',
  searchPlaceholder: 'Buscar empresa o área…',
  methodNote:
    'Cuando no se emitió ningún documento en el período, el cumplimiento NO es 100 %: es «Sin registros». No haber emitido nada no es haber cumplido.',
  defaultSort: 'periodLabel',
  columns: [
    ...CONTEXT_COLUMNS,
    { key: 'contractsIssued', header: 'Contratos emitidos', kind: 'integer', sortable: true },
    { key: 'contractsSigned', header: 'Contratos firmados', kind: 'integer', sortable: true },
    {
      key: 'contractsCompliance',
      header: 'Cumpl. contratos',
      kind: 'metric',
      metricFormat: 'percent',
      sortable: true,
    },
    { key: 'annexesIssued', header: 'Anexos emitidos', kind: 'integer', sortable: true },
    { key: 'annexesSigned', header: 'Anexos firmados', kind: 'integer', sortable: true },
    {
      key: 'annexesCompliance',
      header: 'Cumpl. anexos',
      kind: 'metric',
      metricFormat: 'percent',
      sortable: true,
    },
    { key: 'settlements', header: 'Finiquitos', kind: 'integer', sortable: true },
    { key: 'settlementsOnTime', header: 'Finiquitos en plazo', kind: 'integer', sortable: true },
    {
      key: 'settlementsCompliance',
      header: 'Finiq. en plazo',
      kind: 'metric',
      metricFormat: 'percent',
      sortable: true,
    },
  ],
};
